// ═══════════════════════════════════════════════════════════════
// FILE: src/components/OnboardingWizard.jsx
// FILE VERSION: ONBOARDINGWIZARD-v1.0
// CHARLIE VERSION: v1.7.48 (real, per #1337; note the real v1.7.48
// double-reservation with #1407 -- not resolved here, out of scope)
// CREATED: 2026-08-18
//
// Real, #1337. Forced first-login flow -- App.js gates on
// currentUser.profile.onboarding_completed === false and renders this
// instead of Shell. Guided step-by-step per Shane's explicit ask (a new
// hire needs this, not a dense settings form) -- reuses the exact real
// field set/types already live in TeamMemberProfile.jsx, and the real
// avatar-upload pattern already live in UsersAdmin.jsx, rather than
// inventing a second version of either.
//
// Real, deliberately does NOT collect SSN or any payroll/bank field --
// Gusto owns that, separately, per the row's own explicit scope.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const inputStyle = { width: '100%', border: '1px solid #2a3347', background: '#0f1423', color: '#f0f2f5', borderRadius: 8, padding: '11px 14px', fontSize: 14, boxSizing: 'border-box', outline: 'none' };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gov-slate-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };

function Field({ label, children }) {
  return <div style={{ marginBottom: 16 }}><label style={labelStyle}>{label}</label>{children}</div>;
}

const STEPS = ['welcome', 'profile', 'contact', 'photo', 'done'];

export default function OnboardingWizard({ user, profile, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const [form, setForm] = useState({
    title: profile.title || '',
    phone: profile.phone || '',
    personal_email: profile.personal_email || '',
    hire_date: profile.hire_date || '',
    birthday: profile.birthday || '',
    home_address: profile.home_address || '',
    emergency_contact_name: profile.emergency_contact_name || '',
    emergency_contact_phone: profile.emergency_contact_phone || '',
  });

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) return;
    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `avatars/${user.id}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      setAvatarUrl(`${data.publicUrl}?t=${Date.now()}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function handleFinish() {
    setSaving(true);
    setError('');
    const { error: updateErr } = await supabase.from('user_profiles').update({
      ...form,
      hire_date: form.hire_date || null,
      birthday: form.birthday || null,
      avatar_url: avatarUrl || null,
      onboarding_completed: true,
    }).eq('id', user.id);
    setSaving(false);
    if (updateErr) { setError(updateErr.message); return; }
    onComplete({ ...profile, ...form, avatar_url: avatarUrl, onboarding_completed: true });
  }

  const shell = (children, { showBack = true, nextLabel = 'Continue', onNext } = {}) => (
    <div style={{ minHeight: '100vh', background: '#0f1423', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 440, padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, justifyContent: 'center' }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ width: 28, height: 4, borderRadius: 2, background: i <= step ? '#c9a227' : '#2a3347' }} />
          ))}
        </div>
        <div style={{ background: '#1e2433', borderRadius: 16, padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          {children}
          {error && (
            <div style={{ background: '#7f1d1d22', border: '1px solid #b91c1c44', color: '#fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '16px 0' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            {showBack && step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{ flex: '0 0 auto', background: 'transparent', border: '1px solid #2a3347', color: 'var(--gov-slate-400)', borderRadius: 8, padding: '11px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Back
              </button>
            )}
            <button
              onClick={onNext}
              disabled={saving}
              style={{ flex: 1, background: '#c9a227', color: '#0e1620', border: 'none', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving...' : nextLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (STEPS[step] === 'welcome') {
    return shell(
      <>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#ffffff', marginBottom: 8 }}>Welcome to CHARLIE, {profile.full_name?.split(' ')[0] || 'there'}.</div>
        <div style={{ fontSize: 13, color: 'var(--gov-slate-400)', lineHeight: 1.6 }}>
          Before you get started, let's confirm a few real details. Takes about two minutes.
          You'll get a separate, secure invite from Gusto for pay and benefits setup -- that's not part of this.
        </div>
      </>,
      { showBack: false, nextLabel: "Let's go", onNext: () => setStep(1) }
    );
  }

  if (STEPS[step] === 'profile') {
    return shell(
      <>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f5', marginBottom: 16 }}>Your role</div>
        <Field label="Title / Position">
          <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Hire Date">
          <input type="date" style={inputStyle} value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
        </Field>
        <Field label="Birthday">
          <input type="date" style={inputStyle} value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
        </Field>
      </>,
      { onNext: () => setStep(2) }
    );
  }

  if (STEPS[step] === 'contact') {
    return shell(
      <>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f5', marginBottom: 16 }}>Contact info</div>
        <Field label="Phone">
          <input style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="Personal Email">
          <input type="email" style={inputStyle} value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} />
        </Field>
        <Field label="Home Address">
          <input style={inputStyle} value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} />
        </Field>
        <Field label="Emergency Contact Name">
          <input style={inputStyle} value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
        </Field>
        <Field label="Emergency Contact Phone">
          <input style={inputStyle} value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
        </Field>
      </>,
      { onNext: () => setStep(3) }
    );
  }

  if (STEPS[step] === 'photo') {
    return shell(
      <>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f5', marginBottom: 16 }}>Profile picture</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
            disabled={uploadingAvatar}
            style={{
              width: 88, height: 88, borderRadius: '50%', border: 'none', padding: 0, overflow: 'hidden',
              background: avatarUrl ? 'transparent' : '#0f1423', cursor: uploadingAvatar ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gov-slate-500)', fontSize: 12,
            }}
          >
            {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (uploadingAvatar ? 'Uploading...' : 'Click to upload')}
          </button>
        </div>
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--gov-slate-600)' }}>Optional -- you can always add this later.</div>
        <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
      </>,
      { onNext: () => setStep(4) }
    );
  }

  // done
  return shell(
    <>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#ffffff', marginBottom: 8 }}>All set.</div>
      <div style={{ fontSize: 13, color: 'var(--gov-slate-400)', lineHeight: 1.6 }}>
        You're ready to get into CHARLIE. Keep an eye out separately for the Gusto invite to finish pay/benefits setup.
      </div>
    </>,
    { nextLabel: 'Enter CHARLIE', onNext: handleFinish }
  );
}
